import { Card, Avatar, Tag, Space, Typography, Tooltip } from 'antd';
import {
  UserOutlined,
  ToolOutlined,
  BuildOutlined,
  CalendarOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { NameLink } from '../common';
import { ResponsiveTable, type ResponsiveColumnType } from '../common/responsive';

const { Text } = Typography;

interface Activity {
  id: string;
  activityType: 'created' | 'updated' | 'deleted';
  entityType: 'equipment' | 'part' | 'work_schedule';
  entityId: string;
  entityName: string;
  entityDisplayId: string | null;
  userId: string;
  userName: string;
  userEmail: string;
  timestamp: Date;
}

interface ActivitiesData {
  activities: Activity[];
}

interface UserActivityLogProps {
  data?: ActivitiesData;
  loading?: boolean;
  limit?: number;
}

const entityTypeConfig = {
  equipment: {
    label: '設備',
    icon: <ToolOutlined />,
    color: '#1890ff',
  },
  part: {
    label: '部品・消耗品',
    icon: <BuildOutlined />,
    color: '#52c41a',
  },
  work_schedule: {
    label: '作業予定',
    icon: <CalendarOutlined />,
    color: '#faad14',
  },
};

const activityTypeConfig = {
  created: {
    label: '作成',
    icon: <PlusCircleOutlined />,
    color: '#52c41a',
  },
  updated: {
    label: '編集',
    icon: <EditOutlined />,
    color: '#1890ff',
  },
  deleted: {
    label: '削除',
    icon: <DeleteOutlined />,
    color: '#ff4d4f',
  },
};

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - new Date(date).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'たった今';
  if (diffMins < 60) return `${diffMins}分前`;
  if (diffHours < 24) return `${diffHours}時間前`;
  if (diffDays < 7) return `${diffDays}日前`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}週間前`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}ヶ月前`;
  return `${Math.floor(diffDays / 365)}年前`;
}

export function UserActivityLog({ data, loading, limit = 20 }: UserActivityLogProps) {
  const navigate = useNavigate();

  const activities = data?.activities || [];
  const displayActivities = activities.slice(0, limit);

  const resolveEntityRoute = (activity: Activity) => {
    const routes = {
      equipment: `/app/equipments/${activity.entityId}`,
      part: `/app/parts/${activity.entityId}`,
      work_schedule: `/app/schedule/${activity.entityId}`,
    } as const;

    return routes[activity.entityType];
  };

  const columns: ResponsiveColumnType<Activity>[] = [
    {
      title: 'ユーザー',
      key: 'user',
      fixed: 'left', //これがあるとtableのtable-layout:fixedになる
      width: 120,
      mobileRole: 'extra',
      mobilePriority: 1,
      render: (_, record) => (
        <Space size="small">
          <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <div>
            <div>
              <Text strong style={{ fontSize: '13px' }}>
                {record.userName}
              </Text>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: '11px' }}>
                {record.userEmail}
              </Text>
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 60,
      mobileRole: 'tag',
      mobilePriority: 2,
      render: (_, record) => {
        const config = activityTypeConfig[record.activityType];
        return (
          <Tag icon={config.icon} color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: '対象',
      key: 'entity',
      width: 200,
      mobileRole: 'title',
      mobilePriority: 3,
      render: (_, record) => {
        const config = entityTypeConfig[record.entityType];
        const isDeleted = record.activityType === 'deleted';

        return (
          <Space size="small">
            <span style={{ color: config.color }}>{config.icon}</span>
            <div>
              <div>
                <Tag color={config.color} style={{ marginRight: 4 }}>
                  {config.label}
                </Tag>
                {isDeleted ? (
                  <Text delete type="secondary">
                    {record.entityName}
                  </Text>
                ) : (
                  <NameLink
                    name={record.entityName}
                    onClick={() => {
                      navigate(resolveEntityRoute(record));
                    }}
                  />
                )}
              </div>
              {record.entityDisplayId && (
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  ID: {record.entityDisplayId}
                </Text>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: '日時',
      key: 'timestamp',
      width: 60,
      mobileRole: 'extra',
      mobilePriority: 4,
      render: (_, record) => {
        const timeAgo = getTimeAgo(record.timestamp);
        const fullDate = new Date(record.timestamp).toLocaleString('ja-JP');
        return (
          <Tooltip title={fullDate}>
            <Text type="secondary">{timeAgo}</Text>
          </Tooltip>
        );
      },
    },
  ];
/*
ResponsiveTableの注意
tableのstyleでtable-layoutをfixedにする=columnに一つはfixedの項目で固定する
全てのcolumnにwidthをつけて、そのwidthの合計をscrollXの値にする
以上を満たさないとtableがセルからはみ出て、widthが適用されない
*/
  return (
    <Card
      title={
        <Space>
          <HistoryOutlined />
          ユーザー操作ログ
        </Space>
      }
      extra={
        <Text type="secondary" style={{ fontSize: '12px' }}>
          直近{displayActivities.length}件
        </Text>
      }
    >
      <ResponsiveTable<Activity>
        columns={columns}
        dataSource={displayActivities}
        rowKey="id"
        pagination={false}
        size="small"
        loading={loading}
        scrollX={440}
        mobileCardConfig={{
          onClick: (record) => {
            if (record.activityType === 'deleted') return;
            navigate(resolveEntityRoute(record));
          },
        }}
        locale={{
          emptyText: '操作ログがありません',
        }}
      />
    </Card>
  );
}
